import { primeConnectionSound, playConnectionSound } from './connection-sound';
import { isGreetingOpener } from './opening-message';
import { parseConfig } from './config';
import type { EmbedConfig } from './config';
import { createRegistry } from './registry';
import { createEventBus } from './event-bus';
import { createRoomManager } from './room';
import { createSessionClient } from './session-client';
import type { PreparedSession } from './session-client';
import { createPushApi } from './push-api';
import { registerCallSiteFunctionHandler } from './rpc-handlers';
import { createRenderAdapter } from './render/select';
import type { PresentationProps } from './render/adapter';
import { installSceneBuiltin } from './builtins/scene';
import { createSceneSurface, type SceneSurface } from './scene/surface';
import { installThemeBuiltin } from './builtins/theme';
import { installAvatarBuiltin } from './builtins/avatar';
import type { AvatarBuiltinHandle } from './builtins/avatar';
import { installAudioBuiltin } from './builtins/audio';
import type { AudioBuiltinHandle } from './builtins/audio';
import { PROTOCOL_VERSION } from './postmessage/envelope';
import { attachAnalyticsBridge, createAnalyticsRelay, isAnalyticsOptedOut } from './analytics';
import type { EndedBy } from './analytics';

const VERSION = '1.0.0';

type SessionState = 'disconnected' | 'connecting' | 'connected' | 'disconnecting';

export interface MobeusDiagnostic {
  version: string;
  protocolVersion: number;
  config: EmbedConfig;
  registeredSiteFunctions: string[];
  sessionState: SessionState;
  roomName: string | null;
  isReady: boolean;
}

export function bootstrap(scriptEl: HTMLScriptElement | null) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if ((window as any).Mobeus?.__initialized) {
    console.warn('[Mobeus] already initialized; ignoring second load');
    return;
  }

  const apiKey = scriptEl?.getAttribute('data-api-key');
  if (!apiKey) {
    console.error('[Mobeus] Missing data-api-key attribute on embed script.');
    return;
  }

  const rawConfig = scriptEl?.getAttribute('data-config');
  // `config` is `let` (not `const`) so `Mobeus.configure(partial)` can override
  // selected keys before pre-warm fires. Keys that affect already-mounted
  // subsystems (e.g. `render`) cannot change post-bootstrap; see `configure`.
  let config = parseConfig(rawConfig);

  // Chat mode drops the whole voice half: no mic publish, no agent audio
  // attached, no avatar built-in. Deliberately independent of `config.avatar`
  // and `config.layout` so a partner keeps their docked fullscreen column and
  // its background art and only loses the voice, which is what "toggle to a
  // chat-only experience" actually means. Computed here, above the fullscreen
  // gate, because the gate has to know about it.
  const chatMode = config.mode === 'chat';

  // Fullscreen layout (Phase 6) requires avatar:true + render:"shadow" — same
  // constraint avatar:true already has below, for the same reasons: no avatar
  // video means nothing to take the viewport over with, and iframe mode's
  // cross-document boundary blocks an internal avatar mount exactly like it
  // does for the compact panel. Follow that existing precedent (warn + fall
  // back) instead of silently rendering a blank/broken fullscreen takeover.
  // `mode:"chat"` satisfies the avatar half on its own: the gate exists because
  // an avatar-less fullscreen has nothing to take the screen over with, but a
  // chat-only fullscreen deliberately shows the idle art / `--mobeus-fs-bg`
  // instead. Without this, a docked chat column had to declare an avatar the
  // partner explicitly does not want.
  //
  // The render-mode half still applies to BOTH modes: iframe's cross-document
  // boundary blocks the fullscreen tree either way. So the minimum working
  // chat-column config is `{layout:"fullscreen", render:"shadow",
  // mode:"chat", dock:"#..."}` — `render` defaults to `iframe`, and omitting
  // it still falls back to the corner panel (with the warning below naming
  // what is missing).
  if (config.layout === 'fullscreen' && !((config.avatar === true || chatMode) && config.render === 'shadow')) {
    console.warn(
      '[Mobeus] layout:"fullscreen" requires render:"shadow" plus either avatar:true or mode:"chat"; falling back to layout:"panel"',
    );
    config = { ...config, layout: 'panel' };
  }

  // Dock resolution: a partner opting into a bounded (non-viewport) fullscreen
  // panel names a host-page container via `config.dock`. Resolved once, here,
  // before the render adapter mounts — `select.ts`/`shadow.ts` need the real
  // element to append into, not just the selector string. A missing element
  // (typo, page not yet rendered when embed.js runs, etc.) falls back to the
  // existing full-viewport behavior rather than mounting nothing.
  let dockEl: HTMLElement | null = null;
  if (config.layout === 'fullscreen' && config.dock) {
    dockEl = document.querySelector<HTMLElement>(config.dock);
    if (!dockEl) {
      console.warn(`[Mobeus] dock selector "${config.dock}" not found on the page; falling back to full-viewport fullscreen`);
    }
  }
  const docked = config.layout === 'fullscreen' && !!dockEl;

  // Where the platform API lives. Normally derived from this script's own
  // `src`, because the script is served BY the platform. A self-hosted copy of
  // embed.js sits on the customer's own origin (they downloaded it because a
  // CSP blocks third-party scripts), where deriving from `src` would aim every
  // session call at their server. `data-api-base` names the platform origin
  // explicitly for that case. A trailing path is preserved, so a reverse proxy
  // fronting us under a sub-path works too.
  const baseUrl = (() => {
    const explicit = scriptEl?.getAttribute('data-api-base');
    if (explicit) {
      try {
        // Throws on a relative or malformed value. The protocol check keeps a
        // javascript:/data: value out of iframe.src (CodeQL js/xss-through-dom).
        const parsed = new URL(explicit);
        if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
          throw new Error('unsupported protocol');
        }
        return explicit.replace(/\/+$/, '');
      } catch {
        console.error(
          `[Mobeus] data-api-base must be an absolute URL (e.g. "https://app.mobeus.ai"); ignoring "${explicit}".`,
        );
      }
    }
    const src = scriptEl?.src ?? '';
    return src.replace(/\/embed(\.[a-f0-9]+)?\.js(\?.*)?$/, '') || window.location.origin;
  })();

  const registry = createRegistry();
  const eventBus = createEventBus();
  // Visitor-funnel relay to OUR server (never PostHog, no cookies/storage on the
  // customer's site). No-op when opted out (data-analytics="off", data-config
  // {"analytics":false}, DNT / GPC). See ./analytics.ts.
  const analytics = createAnalyticsRelay({
    baseUrl,
    apiKey,
    optOut: isAnalyticsOptedOut(scriptEl?.getAttribute('data-analytics'), rawConfig),
  });
  attachAnalyticsBridge(analytics, eventBus);
  analytics.track('widget_loaded', {
    render_mode: config.render,
    layout: config.layout,
    mode: config.mode,
    pre_warm: config.preWarm,
    avatar: config.avatar !== false && !chatMode,
  });
  const roomManager = createRoomManager({ eventBus });
  const sessionClient = createSessionClient({ baseUrl, apiKey });
  const pushApi = createPushApi(() => roomManager.getRoom());
  const renderAdapter = createRenderAdapter({ baseUrl, mode: config.render, styling: config.styling, dockEl });

  // Scene surface. Created on the first session response rather than here,
  // because its container selector is per-website server config
  // (`widget.defaults.sceneContainer`), not a `data-config` key — changing it
  // must not cost the partner an edit and a republish. Deliberately
  // independent of `config.render`: fullscreen forces `render:"shadow"`, and
  // scenes need the iframe regardless of what the panel is using.
  let sceneSurface: SceneSurface | null = null;

  // Install enabled built-ins. Built-ins register their site-functions via
  // registry.registerSystem() which bypasses the reserved-name guard, so they
  // can claim 'scene' / 'setTheme' without conflicting with the guard that
  // protects those names from custom registrations.
  if (config.scene) installSceneBuiltin(registry);
  if (config.theme) installThemeBuiltin(registry);
  // Handle to the installed avatar built-in, if any — used by `patch()`
  // below to keep its mount element in sync with ChatPanel's internal
  // `[data-mobeus-avatar-mount]` node as it appears/disappears across
  // re-renders (shadow mode only; see AvatarBuiltinHandle.setMountEl).
  let avatarHandle: AvatarBuiltinHandle | null = null;
  // Handle to the installed plain-audio built-in (non-avatar path), if any —
  // used by the `toggleVolume` handler below to actually mute/unmute the
  // agent's audio output rather than just flipping a display flag. Mutually
  // exclusive with `avatarHandle`: whichever built-in is actually playing
  // audio for this embed instance is the one that needs to hear about it.
  let audioHandle: AudioBuiltinHandle | null = null;
  // Whether ChatPanel should render its own internal avatar mount slot.
  // Only possible in shadow mode: the iframe adapter's presentation tree
  // lives in a separate cross-boundary document that host-page JS (where
  // this bootstrap runs) cannot reach into — see render/select.ts's styling
  // comment for the same boundary applied to CSS.
  const avatarInternalMount = config.avatar === true && config.render === 'shadow' && !chatMode;
  // Guards every branch below rather than wrapping them: in chat mode NEITHER
  // built-in may install. `installAudioBuiltin` is what makes the agent
  // audible when there's no avatar, so skipping only the avatar one would
  // route the agent's speech straight to the speakers instead of silencing it.
  const installVoiceBuiltins = !chatMode;
  if (chatMode && config.avatar !== false) {
    // Covers both `avatar:true` and the `{mount:"selector"}` form. The mount
    // form matters most: that element is the PARTNER'S own node, sized and
    // styled by them, and it would otherwise sit empty for the life of the
    // page with nothing in the console to explain why. Also restores the
    // avatar:true + iframe warning that used to fire here on the non-chat
    // path, which the chat guard would otherwise swallow.
    console.warn('[Mobeus] mode:"chat" disables the avatar; ignoring the `avatar` config for this embed.');
  }
  if (installVoiceBuiltins && config.avatar === true) {
    if (config.render === 'shadow') {
      avatarHandle = installAvatarBuiltin({
        roomManager,
        eventBus,
        mountEl: renderAdapter.getAvatarMountEl?.() ?? null,
      });
    } else {
      console.warn(
        '[Mobeus] avatar:true requires render:"shadow" for an internal avatar mount; disabling avatar. ' +
          'Use avatar:{mount:"css-selector"} to mount into a host-page element in iframe mode.',
      );
      audioHandle = installAudioBuiltin({ roomManager, eventBus });
    }
  } else if (!chatMode && config.avatar && typeof config.avatar === 'object') {
    avatarHandle = installAvatarBuiltin({
      roomManager,
      eventBus,
      mountSelector: config.avatar.mount,
      fit: config.avatar.fit,
      opacity: config.avatar.opacity,
    });
  } else if (!chatMode) {
    // No avatar: the avatar built-in isn't attaching agent audio, so play it
    // here. Otherwise the agent speaks but is never heard (see audio.ts).
    audioHandle = installAudioBuiltin({ roomManager, eventBus });
  }

  // Initial presentation props
  let props: PresentationProps = {
    position: config.position,
    layout: config.layout,
    agentName: null,
    agentState: 'initializing',
    transcripts: [],
    toolActivity: [],
    citations: [],
    isMuted: false,
    isVolumeMuted: false,
    isConnected: false,
    isConnecting: false,
    inputStart: config.inputStart && config.render === 'shadow' && config.layout === 'fullscreen',
    // Docked mode has nowhere for a separate corner-pill affordance to live
    // sensibly inside a partner-owned column, and the whole point is an
    // always-visible inline chat (matching the avatar that's already
    // permanently mounted there) — so start already "expanded" instead of
    // requiring a click to open it.
    //
    // ...unless auto-start is off, in which case an already-expanded panel
    // would be an open, disconnected chat with no obvious way in. Starting
    // collapsed makes the "Let's talk" pill the entry point, and opening it
    // connects — one click, and the label says what it does. Sites on the
    // default `preWarm:true` are unaffected.
    isOverlayExpanded: docked && config.preWarm,
    showToolCalls: config.toolCalls,
    showCitations: config.citations,
    error: null,
    micWarning: null,
    avatarEnabled: false,
    avatarVisible: false,
    avatarThumbnailUrl: null,
    avatarInternalMount,
    docked,
    chatMode,
  };
  function patch(p: Partial<PresentationProps>) {
    props = { ...props, ...p };
    renderAdapter.update(p);
    // Re-sync the avatar built-in's mount element after every re-render:
    // ChatPanel's internal mount div is torn down/recreated as
    // `avatarEnabled`/`isOverlayExpanded` change, so a track that
    // subscribed while it was absent needs a chance to attach once it
    // (re)appears. No-op when avatar isn't in internal-mount mode.
    if (avatarHandle) avatarHandle.setMountEl(renderAdapter.getAvatarMountEl?.() ?? null);
  }

  // Diagnostic state tracking. `sessionState` is driven by the same bus
  // events the host can subscribe to, so it can never diverge from observed
  // behaviour. `roomName` is captured from the latest `sessionStart` payload.
  let sessionState: SessionState = 'disconnected';
  let roomName: string | null = null;

  // Greeting seeding. The configured `website.greeting` is shown immediately as
  // the opening agent bubble (the agent's spoken greeting transcription can be
  // missed by a late-subscribing client — e.g. after the first-visit mic
  // prompt). The transcript handler reconciles the spoken greeting onto this
  // seed so it isn't shown twice.
  const GREETING_ID = 'mobeus-greeting-seed';
  let greetingText: string | null = null;
  let greetingSeeded = false;
  let greetingReconciled = false;
  // Same message, allowing for streaming partials in either direction.
  const isSameGreeting = (a: string, b: string) =>
    a.length > 0 && (a.startsWith(b) || b.startsWith(a));
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  eventBus.on('sessionStart', (p: any) => {
    sessionState = 'connected';
    roomName = p?.roomName ?? null;
  });
  eventBus.on('sessionEnd', () => {
    sessionState = 'disconnected';
    roomName = null;
  });

  // Deliberately does NOT clear `props.error`: state transitions fire
  // constantly during a live call (listening/thinking/speaking), so clearing
  // here would wipe a genuine still-relevant failure (e.g. SEND_TEXT_FAILED
  // for a message that was never delivered) moments after it appeared. "The
  // agent is alive" is not proof that the failed action recovered — errors
  // clear only on paths that disprove them (see the transcript handler and
  // the sendText success path).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  eventBus.on('agentStateChange', ({ state }: any) => patch({ agentState: state }));
  // Ring-buffer transcript handler: dedupe by id, cap at 200 entries.
  // Prevents O(n) growth from streaming transcripts in long sessions.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  eventBus.on('transcript', (t: any) => {
    let entry = t;
    // Reconcile the agent's spoken greeting with the seeded greeting bubble so
    // it isn't duplicated. The seed carries GREETING_ID; the live greeting
    // transcription streams in under its own id. If its text is the same
    // message (a prefix either way — covers streaming partials), remap it onto
    // the seed. If the first agent utterance is something else (e.g. a reply,
    // which is all a late-subscribing client sees), leave the seed and stop.
    if (!greetingReconciled && greetingText && entry.participant === 'agent' && entry.id !== GREETING_ID) {
      const gt = greetingText;
      const tt = String(entry.text ?? '').trim();
      if (isSameGreeting(tt, gt)) {
        entry = { ...entry, id: GREETING_ID };
        if (tt.length >= gt.length) greetingReconciled = true;
      } else {
        greetingReconciled = true;
      }
    }
    const idx = props.transcripts.findIndex((x) => x.id === entry.id);
    const next = idx >= 0
      ? props.transcripts.map((x, i) => (i === idx ? entry : x))
      : [...props.transcripts, entry];
    // An agent-attributed transcript disproves connection-class errors
    // specifically — "no agent connected" / CONNECT_FAILED can't be true
    // while the agent's words are streaming in — so clear those. It does
    // NOT disprove a generic send failure (this segment could be the tail
    // of a reply to an earlier message, unrelated to the one that failed),
    // and a user-typed local echo proves nothing about the agent side, so
    // neither of those clears.
    const disprovedConnectionError =
      entry.participant === 'agent' &&
      props.error !== null &&
      (props.error.code === 'CONNECT_FAILED' || props.error.message === 'no agent connected');
    patch({
      transcripts: next.length > 200 ? next.slice(-200) : next,
      ...(disprovedConnectionError ? { error: null } : {}),
    });
  });
  // Show the configured greeting as the opening agent bubble, whichever of
  // the two copies of it arrives first. The reconciler above handles
  // seed-then-spoken; this handles spoken-then-seed, which the room can
  // still produce (the agent's transcription is subscribed inside
  // `roomManager.connect`, so it can land before this call gets to run).
  // Adopting the bubble that is already on screen is what keeps the count
  // at one — emitting the seed regardless is what produced two.
  function seedGreeting(raw: string | null | undefined): void {
    const greeting = raw?.trim();
    if (!greeting || greetingSeeded) return;
    greetingText = greeting;
    greetingSeeded = true;
    greetingReconciled = false;
    const spoken = props.transcripts.some(
      (t) => t.participant === 'agent' && isSameGreeting(String(t.text ?? '').trim(), greeting),
    );
    if (spoken) {
      // Its own id keeps receiving the remaining stream, so it completes on
      // its own. Nothing to seed, and nothing left to reconcile.
      greetingReconciled = true;
      return;
    }
    eventBus.emit('transcript', {
      id: GREETING_ID,
      text: greeting,
      participant: 'agent',
      isFinal: true,
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  eventBus.on('toolCall', (e: any) => patch({ toolActivity: [...props.toolActivity, e].slice(-100) }));
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  eventBus.on('citation', (c: any) => patch({ citations: [...props.citations, c].slice(-50) }));
  eventBus.on('sessionStart', () => patch({ isConnected: true, error: null }));
  eventBus.on('sessionEnd', () => patch({ isConnected: false, agentState: 'initializing' }));
  // Scenes. The surface is created later (the container selector is
  // server-sourced and only arrives with the session response), so this
  // subscription can fire before there is anywhere to render — which is fine
  // and also the no-container case, where it stays a no-op forever.
  //
  // A partner-registered handler wins outright, same precedence the theme
  // built-in uses. They asked for the raw DSL; rendering our cards on top of
  // whatever they drew would be worse than doing nothing.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  eventBus.on('scene', (msg: any) => {
    const handler = registry.getSceneHandler();
    if (handler) {
      // Their contract is `(dsl: string)`. Skeletons carry no DSL, so they
      // are ours to swallow rather than something to invent a payload for.
      if (typeof msg?.dsl === 'string') {
        try {
          handler(msg.dsl);
        } catch (e) {
          console.error('[Mobeus] scene handler threw:', e);
        }
      }
      return;
    }
    sceneSurface?.push(msg);
  });
  // Surface connect/runtime failures in the UI instead of only logging them.
  // MIC_ENABLE_FAILED is carved out to its own `micWarning` channel: it fires
  // on EVERY connect (see the eager setMicrophoneEnabled call below) since a
  // browser mic-permission prompt/denial isn't a connection failure — a
  // text-only user declining it would otherwise see the same alarming
  // "something's broken" banner a real CONNECT_FAILED/SEND_TEXT_FAILED gets,
  // for something that doesn't affect them at all. `Mobeus.on('error', ...)`
  // still receives the raw event unchanged; only the widget's own banner
  // routing is affected.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  eventBus.on('error', (e: any) => {
    if (e?.code === 'MIC_ENABLE_FAILED') {
      patch({ micWarning: String(e?.message ?? 'Microphone permission was not granted') });
      return;
    }
    patch({ error: { code: String(e?.code ?? 'UNKNOWN'), message: String(e?.message ?? 'Something went wrong') } });
  });
  // Allow the next connect to re-seed the greeting.
  eventBus.on('sessionEnd', () => {
    greetingSeeded = false;
    greetingText = null;
    greetingReconciled = false;
  });

  // The in-flight connect attempt, if any. Concurrent callers get this SAME
  // promise back rather than an immediate void return — critical for actions
  // racing a connect (e.g. sendText fired while toggleExpand's connect is
  // still in flight): they must wait for the room to exist before pushing,
  // or tellAgent hits requireRoom's 'not connected' (which the retry loop
  // deliberately does not retry) and the message is lost.
  // Set when the user explicitly hangs up (`endCall` / `close`), cleared by
  // any explicit connect. Distinguishes "no room because we haven't started
  // yet" (expanding should start one) from "no room because the visitor just
  // ended the call" (expanding must not silently start another).
  let userEndedSession = false;

  let inputStarting = false;
  let startupGreeting: string | null = null;
  let finishInputMedia: ((ready: boolean) => void) | null = null;
  let inputMediaReady = false;
  let inputCancelled = false;
  const inputStart = config.inputStart && config.render === 'shadow' && config.layout === 'fullscreen';

  eventBus.on('avatarMediaReady', () => {
    inputMediaReady = true;
    finishInputMedia?.(true);
  });

  let connectPromise: Promise<void> | null = null;
  let connectAttemptId = 0;
  let avatarReadyAttemptId = 0;
  let expandAfterConnectAttemptId = 0;

  function expandAfterConnectIfNeeded(attemptId: number): void {
    if (expandAfterConnectAttemptId !== attemptId) return;
    expandAfterConnectAttemptId = 0;
    if (props.isOverlayExpanded || userEndedSession || !roomManager.getRoom()) return;
    patch({ isOverlayExpanded: true });
    analytics.track('widget_opened');
  }

  eventBus.on('avatarReady', () => {
    const attemptId = connectAttemptId;
    avatarReadyAttemptId = attemptId;
    if (!inputStarting) {
      if (props.isConnecting) patch({ isConnecting: false });
      expandAfterConnectIfNeeded(attemptId);
    }
  });
  eventBus.on('sessionEnd', () => {
    inputCancelled = true;
    finishInputMedia?.(false);
    expandAfterConnectAttemptId = 0;
    if (props.isConnecting) patch({ isConnecting: false });
  });

  function connectAndDispatch(deferGreeting = false): Promise<void> {
    if (connectPromise) return connectPromise;
    // A Room object survives a transport-level drop: `room.ts` only nulls its
    // reference inside its own `disconnect()`, so after a server-side end
    // (idle timeout, agent crash, network blip) `getRoom()` keeps handing back
    // a dead Room. Treating that as "already connected" made Start / Connect
    // dead buttons — the user clicks and nothing happens at all, forever.
    const existing = roomManager.getRoom() as { state?: string } | null;
    if (existing && existing.state !== 'disconnected') return Promise.resolve();
    connectPromise = doConnectAndDispatch(deferGreeting).finally(() => {
      connectPromise = null;
    });
    return connectPromise;
  }
  async function doConnectAndDispatch(deferGreeting: boolean): Promise<void> {
    userEndedSession = false;
    sessionState = 'connecting';
    const attemptId = connectAttemptId + 1;
    connectAttemptId = attemptId;
    avatarReadyAttemptId = 0;
    let holdConnectingForAvatar = false;
    patch({ error: null, micWarning: null, isConnecting: true }); // clear any stale error/mic-warning from a previous attempt
    try {
      const manifest = registry.serializeManifest({ scene: config.scene, theme: config.theme });
      let prepared: PreparedSession;
      if (config.preWarm && !deferGreeting) {
        prepared = await sessionClient.prepare(manifest);
        await roomManager.connect({
          wsUrl: prepared.wsUrl,
          token: prepared.token,
          roomName: prepared.roomName,
          agentName: prepared.agent.name,
          sessionId: prepared.roomName,
        });
      } else {
        prepared = deferGreeting
          ? await sessionClient.unified(manifest, { deferGreeting: true })
          : await sessionClient.unified(manifest);
        if (deferGreeting) {
          if (!prepared.widget?.startup) throw new Error('This server does not support input startup yet.');
          startupGreeting = prepared.widget.startup.greeting;
        }
        await roomManager.connect({
          wsUrl: prepared.wsUrl,
          token: prepared.token,
          roomName: prepared.roomName,
          agentName: prepared.agent.name,
          sessionId: prepared.roomName,
        });
      }
      // Applied here, BEFORE the eager mic publish below, because that call
      // reads `props.isMuted`. The rest of `prepared.widget.defaults` is
      // applied further down, after activate() — too late to keep the
      // microphone from going live on a website configured to start muted.
      // Skipped in chat mode, where neither flag has anything to control:
      // no mic is ever published and no audio built-in is installed, and
      // the controls that would show the state are hidden. The props stay
      // at their unmuted defaults there — see the chat-mode test.
      const startDefaults = prepared.widget?.defaults;
      if (startDefaults && !chatMode) {
        patch({
          isMuted: !!startDefaults.micMuted,
          isVolumeMuted: !!startDefaults.volumeMuted,
        });
      }

      // Seed the configured greeting as the opening agent bubble. Not
      // hardcoded — comes from the website's `greeting` via the session API
      // (`prepared.widget.greeting`). Guarantees the greeting shows even when
      // the agent's spoken-greeting transcription is missed by a late
      // subscriber; the transcript handler folds the spoken copy onto this
      // seed (GREETING_ID) so it never appears twice.
      //
      // Seeded HERE, the first statement after the room is up, because the
      // agent starts talking the moment it joins and its transcription
      // streams in as soon as we subscribe. This used to run at the end of
      // the connect flow, behind an awaited `setMicrophoneEnabled` — a
      // ~240ms hole measured on injuryinfo.webflow.io — and a spoken greeting
      // that landed in that hole found `greetingText` still null, skipped
      // reconciliation entirely, and left the seed to append a second
      // identical bubble. That is the duplicate greeting partners have been
      // reporting; it was ordering, not the prefix match.
      if (!deferGreeting) seedGreeting(prepared.widget?.greeting);

      let room = roomManager.getRoom()!;

      // Publish the microphone as soon as the room/localParticipant exist —
      // that's the real lower bound, and every async step after this
      // (activate, RPC-handler registration, greeting seed) is dead time
      // some browsers count against the click's user-activation window for
      // getUserMedia. Without this the mic track is never created — the
      // ONLY other call to setMicrophoneEnabled is the Mute toggle, so
      // historically the agent heard nothing until the user hit
      // Mute→Unmute (unmute published the track for the first time).
      // Respect props.isMuted so connecting while muted stays muted.
      // Skipped entirely in chat mode — this call is what raises the
      // browser's mic-permission prompt, and a text-only user should never
      // see one.
      if (!chatMode) {
        const enableMic = (async () => {
          try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            await (room.localParticipant as any).setMicrophoneEnabled(!props.isMuted);
            // Only an unmuted start raises the browser prompt, so only then does
            // success mean "permission granted". Denial arrives via the error bridge.
            if (roomManager.getRoom() !== room) return;
            if (!props.isMuted) analytics.track('mic_permission_result', { granted: true });
          } catch (e) {
            if (roomManager.getRoom() !== room) return;
            eventBus.emit('error', { code: 'MIC_ENABLE_FAILED', message: String((e as Error).message ?? e) });
          }
        })();
        // Typed startup must not wait for the microphone permission prompt.
        if (!deferGreeting) await enableMic;
      }

      // `isVolumeMuted` above is only a display flag until it reaches the
      // built-in that owns the audio element — same call `toggleVolume` makes.
      //
      // Pushed unconditionally, NOT only when muted. The built-ins hold
      // `outputMuted` in a closure that survives `sessionEnd` (teardown()
      // drops the audio elements, not the flag) and apply it to every track
      // subscribed afterwards. Pushing only `true` let the two states drift:
      // mute the speaker, hang up, hit Start, and the patch above resets the
      // flag to the server default while the built-in stays latched — an
      // unmuted speaker icon over an agent nobody can hear, recoverable only
      // by toggling twice. Sending the resolved value re-syncs both ways.
      avatarHandle?.setOutputMuted(props.isVolumeMuted);
      audioHandle?.setOutputMuted(props.isVolumeMuted);

      if (config.preWarm && !deferGreeting) {
        try {
          await sessionClient.activate(prepared.roomName);
        } catch (e) {
          // The pre-warmed room can be gone by the time the user clicks Start
          // (reaped as abandoned, or expired) — activate then 404s. Recover
          // with the unified flow rather than surface an error for a room
          // the user never saw. Only 404: anything else (402 credits, 403,
          // 5xx) is a real answer that must not be masked, and on a two-brain
          // site a 500 can mean the Speak agent is already joining this room.
          if ((e as { status?: number })?.status !== 404) throw e;
          console.warn('[mobeus] pre-warmed room is gone, falling back to unified session:', e);
          analytics.track('widget_prewarm_fallback');
          // Silent: the host page must not see sessionEnd for a room it
          // never got a session in.
          await roomManager.disconnect({ silent: true });
          prepared = await sessionClient.unified(manifest);
          await roomManager.connect({
            wsUrl: prepared.wsUrl,
            token: prepared.token,
            roomName: prepared.roomName,
            agentName: prepared.agent.name,
            sessionId: prepared.roomName,
          });
          room = roomManager.getRoom()!;
          if (!chatMode) {
            try {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              await (room.localParticipant as any).setMicrophoneEnabled(!props.isMuted);
              if (!props.isMuted) analytics.track('mic_permission_result', { granted: true });
            } catch (err) {
              eventBus.emit('error', { code: 'MIC_ENABLE_FAILED', message: String((err as Error).message ?? err) });
            }
          }
        }
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      registerCallSiteFunctionHandler(room.localParticipant as any, registry);

      // Avatar defaults from the website's config — feeds Phase 2's idle
      // background image / avatar-enabled gating. Not read from `data-config`;
      // this is server-sourced per-website configuration.
      const defaults = prepared.widget?.defaults;
      if (defaults) {
        holdConnectingForAvatar = !chatMode && !!avatarHandle && !!defaults.avatarEnabled && !!defaults.avatarVisible;
        patch({
          // `avatarEnabled` drives the avatar mount slot. In chat mode no
          // avatar built-in exists to fill it, so honouring the server
          // default here would render an empty black box over the idle art.
          avatarEnabled: !chatMode && !!defaults.avatarEnabled,
          avatarVisible: !chatMode && !!defaults.avatarVisible,
          avatarThumbnailUrl: defaults.avatarThumbnailUrl ?? null,
        });

        // Mount once and keep it across reconnects: scenes are meant to
        // persist after a session ends (hosted sites do the same), and
        // re-mounting would blank the partner's page on every reconnect.
        if (!sceneSurface && defaults.sceneContainer) {
          sceneSurface = createSceneSurface({
            baseUrl,
            selector: defaults.sceneContainer,
            // Mirrors hosted sites' `informTele` byte for byte, because the
            // agent's listener (`agent/main.py`, topic `tele:feedback`) reads
            // `payload.message` and injects it as a system message.
            onFeedback: ({ reason, detail }) => {
              const room = roomManager.getRoom();
              if (!room?.localParticipant) return;
              try {
                const payload = Uint8Array.from(
                  new TextEncoder().encode(
                    JSON.stringify({ message: `[scene:${reason}] ${detail}`, timestamp: Date.now() }),
                  ),
                );
                room.localParticipant.publishData(payload, { reliable: true, topic: 'tele:feedback' });
              } catch (e) {
                console.warn('[Mobeus] failed to publish scene feedback', e);
              }
            },
          });
        }
      }
    } catch (e) {
      // Reset sessionState on failure; `sessionStart` never fired so the
      // subscriber above won't flip it to 'connected'.
      sessionState = 'disconnected';
      if (expandAfterConnectAttemptId === attemptId) expandAfterConnectAttemptId = 0;
      eventBus.emit('error', { code: 'CONNECT_FAILED', message: String((e as Error).message ?? e) });
    } finally {
      if (inputStarting || (holdConnectingForAvatar && avatarReadyAttemptId !== attemptId)) {
        return;
      } else {
        patch({ isConnecting: false });
        expandAfterConnectIfNeeded(attemptId);
      }
    }
  }

  async function startFromInput(text: string): Promise<void> {
    if (!inputStart || inputStarting) return;
    const message = text.trim() || "Let's talk";
    // A collapsed live session is still a conversation, not another opening.
    if (props.isConnected && !props.isConnecting) {
      patch({ isOverlayExpanded: true });
      eventBus.emit('transcript', { id: `user-typed-${Date.now()}`, text: message, participant: 'user', isFinal: true });
      const result = await pushApi.tellAgent(message);
      if (!result.success) eventBus.emit('error', { code: 'SEND_TEXT_FAILED', message: result.error || 'Failed to reach the agent' });
      return;
    }
    inputStarting = true;
    inputMediaReady = false;
    inputCancelled = false;
    startupGreeting = null;
    primeConnectionSound();
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      await connectAndDispatch(true);
      if (startupGreeting === null || !roomManager.getRoom() || props.error || inputCancelled) return;
      if (props.avatarEnabled && props.avatarVisible && avatarHandle && !inputMediaReady) {
        const ready = await new Promise<boolean>((resolve) => {
          finishInputMedia = resolve;
          timer = setTimeout(() => resolve(false), 20000);
        });
        if (!ready) {
          if (!inputCancelled) {
            await roomManager.disconnect();
            eventBus.emit('error', { code: 'CONNECT_FAILED', message: 'Avatar could not connect. Please try again.' });
          }
          return;
        }
      }
      if (inputCancelled) return;
      patch({ isConnecting: false, isOverlayExpanded: true });
      analytics.track('widget_opened');
      if (!chatMode && !props.isVolumeMuted) playConnectionSound();
      eventBus.emit('transcript', { id: `user-typed-${Date.now()}`, text: message, participant: 'user', isFinal: true });
      const greet = isGreetingOpener(message) && !!startupGreeting;
      // This RPC starts after transcription is attached. Let its transcript
      // own the greeting bubble, including the partial and final updates.
      const result = greet ? await pushApi.agentSay(startupGreeting!, { userInput: message }) : await pushApi.tellAgent(message);
      if (!result.success) eventBus.emit('error', { code: 'SEND_TEXT_FAILED', message: result.error || 'Failed to reach the agent' });
    } catch (error) {
      eventBus.emit('error', { code: 'CONNECT_FAILED', message: String((error as Error).message || error) });
    } finally {
      if (timer) clearTimeout(timer);
      finishInputMedia = null;
      inputStarting = false;
      patch({ isConnecting: false });
    }
  }

  async function disconnectAndUpdate(
    opts: { suppressAutoConnect?: boolean; endedBy?: EndedBy } = {},
  ): Promise<void> {
    // Wait out an in-flight connect before deciding there is nothing to tear
    // down. Without this, hanging up DURING a connect (a cold start is
    // seconds long, and the close button is on screen for all of it) found
    // `getRoom() === null`, early-returned, and let the connect run to
    // completion behind a closed widget: room live, mic published, agent
    // greeting out loud, and nothing left that could stop it — re-opening
    // wouldn't reconnect and wouldn't disconnect either. That is the original
    // "disconnect doesn't work, the avatar is still engaged" bug relocated
    // into the connect window. `doConnectAndDispatch` swallows its own
    // errors, so this never rejects.
    if (connectPromise) await connectPromise;
    // Set only when there was actually a session to end. Ordering matters: a
    // host bridge that calls `Mobeus.disconnect()` defensively on load (or
    // twice) would otherwise latch the flag with no session ever having run,
    // permanently suppressing the collapsed bubble's auto-connect. You cannot
    // "hang up" a call that was not in progress.
    if (!roomManager.getRoom()) return;
    if (opts.endedBy) analytics.noteEndedBy(opts.endedBy);
    if (opts.suppressAutoConnect) userEndedSession = true;
    sessionState = 'disconnecting';
    try {
      await roomManager.disconnect();
    } finally {
      // `roomManager.disconnect` normally emits `sessionEnd`, which resets
      // sessionState via the subscriber above. But that emit is deduped per
      // session (`sessionEnded` in room.ts), so when the room had ALREADY
      // dropped on its own — idle timeout, network blip, a connect that never
      // succeeded — the event never fires a second time and sessionState would
      // stay 'disconnecting' for the rest of the page's life. `diagnostic()`
      // is the surface support reads to debug a live site, so it must not lie.
      // In a `finally` because a rejecting disconnect would otherwise wedge
      // the widget at 'disconnecting' with a non-null room, making every
      // later connect early-return: closed-looking, and unreconnectable.
      // Guarded on the current value so a connect that started during the
      // await (which sets 'connecting') isn't clobbered to 'disconnected'.
      if (sessionState === 'disconnecting') {
        sessionState = 'disconnected';
        roomName = null;
      }
    }
  }

  renderAdapter.mount(props);
  renderAdapter.onEvent(async (ev) => {
    if (ev.type === 'toggleExpand') {
      const opening = !props.isOverlayExpanded;
      // Opening the panel still auto-connects by default — that's how the
      // collapsed Bubble has always started a session, and gating it on
      // `preWarm` would cost panel-layout partners on `preWarm:false` their
      // one-click start. Two things suppress it:
      //
      // `connectOnOpen:false` — the partner wants the panel to open into a
      // visible idle state and the Start button to be the only way in. It is
      // a separate key from `preWarm` precisely because `preWarm:false`
      // already means "don't start on page load" for partners who still want
      // open-to-connect, and reusing it would silently change their widget.
      //
      // `userEndedSession` — the user DELIBERATELY ended the session: now
      // that `close` really disconnects, a visitor who hung up and re-opened
      // the panel to re-read the transcript would otherwise silently open a
      // fresh billable session. Restarting after an explicit hang-up is the
      // Start/Connect button's job.
      if (
        opening &&
        config.connectOnOpen &&
        !roomManager.getRoom() &&
        !userEndedSession
      ) {
        analytics.track('conversation_requested');
        // Fullscreen avatar sites use the collapsed "Let's talk" pill as the
        // real start control. Keep that pill in its loading state until the
        // avatar video can play, then open the chat. Opening first exposes the
        // live-avatar cold start, which is exactly what the still image was
        // meant to hide.
        if (config.layout === 'fullscreen' && !!avatarHandle && !chatMode) {
          expandAfterConnectAttemptId = connectAttemptId + 1;
        } else {
          patch({ isOverlayExpanded: true });
          analytics.track('widget_opened');
        }
        await connectAndDispatch();
      } else {
        if (!opening) expandAfterConnectAttemptId = 0;
        patch({ isOverlayExpanded: opening });
        if (opening) analytics.track('widget_opened');
      }
    } else if (ev.type === 'connect') {
      analytics.track('conversation_requested');
      await connectAndDispatch();
    } else if (ev.type === 'endCall') {
      await disconnectAndUpdate({ endedBy: 'user_end' });
    } else if (ev.type === 'close') {
      // Collapse first so the UI responds immediately rather than waiting on
      // the teardown round-trip; clear the error banner because a send that
      // failed against the session the visitor just ended is not a fault to
      // greet them with on re-open.
      //
      // `suppressAutoConnect` is passed here and nowhere else: `close` is the
      // only action that both ends the call and hides the widget, so re-opening
      // is the very next thing possible and must not start a new session.
      // `endCall` keeps the panel open with its own Connect button, so
      // suppressing there would only cost panel partners a one-click restart.
      expandAfterConnectAttemptId = 0;
      patch({ isOverlayExpanded: false, error: null });
      await disconnectAndUpdate({ suppressAutoConnect: true, endedBy: 'user_close' });
    } else if (ev.type === 'startConversation') {
      await startFromInput(ev.text);
    } else if (ev.type === 'sendText') {
      // Chat panel's text input → push to agent as user-visible message
      const text = ev.text?.trim();
      if (!text) return;
      if (!roomManager.getRoom()) await connectAndDispatch();
      // Local-echo: STT events never fire for typed text, so the user
      // wouldn't see their own message otherwise. Emit a transcript
      // event with a non-'agent' participant so the chat panel renders
      // it as a user bubble (right-aligned, blue).
      eventBus.emit('transcript', {
        id: `user-typed-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        text,
        participant: 'user',
        isFinal: true,
      });
      // connectAndDispatch() above only waits for the USER's participant to
      // join; the agent worker joins seconds later. Waiting that out now
      // lives in push-api.ts's `perform`, so EVERY push method gets it and
      // not just typed messages (a host bridge calling `informAgent` on load
      // hit exactly the same window and silently lost the call).
      const result = await pushApi.tellAgent(text);
      if (!result.success) {
        eventBus.emit('error', { code: 'SEND_TEXT_FAILED', message: result.error ?? 'Failed to reach the agent' });
      } else {
        // A successful push is proof the agent is reachable — clear any
        // stale error left over from an earlier, unrelated failure.
        patch({ error: null });
      }
    } else if (ev.type === 'toggleMute') {
      // Mic on/off — toggle the local participant's microphone track
      if (chatMode) return; // no mic track was ever published
      const room = roomManager.getRoom();
      if (!room) return;
      const next = !props.isMuted;
      const enabling = !next;
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await (room.localParticipant as any).setMicrophoneEnabled(enabling);
        // A successful enable is proof the mic actually works now, clearing
        // any stale MIC_ENABLE_FAILED warning from the initial connect.
        patch(enabling ? { isMuted: next, micWarning: null } : { isMuted: next });
      } catch (e) {
        eventBus.emit('error', { code: 'MIC_TOGGLE_FAILED', message: String((e as Error).message ?? e) });
      }
    } else if (ev.type === 'toggleVolume') {
      // Output mute — flip the presentation flag (for the icon) AND actually
      // mute/unmute the attached remote audio via whichever built-in is
      // playing it. Exactly one of these is non-null for a given embed
      // instance (see where they're assigned above).
      const next = !props.isVolumeMuted;
      avatarHandle?.setOutputMuted(next);
      audioHandle?.setOutputMuted(next);
      patch({ isVolumeMuted: next });
    } else if (ev.type === 'toggleAvatarVisible') {
      // Fullscreen layout's eye icon (Phase 6) — client-side only, same flag
      // ChatPanel's Phase 2 crossfade already reads for `avatarVisible`.
      patch({ avatarVisible: !props.avatarVisible });
    }
  });

  const readyQueue: Array<() => void> = [];
  let isReady = false;
  // Flipped to `true` once pre-warm is dispatched (or short-circuited because
  // the host disabled it). Once set, `Mobeus.configure(...)` becomes a no-op
  // with a warning — config can only be overridden before pre-warm runs.
  let preWarmFired = false;

  function fireReadyHandler(fn: () => void) {
    try { fn(); } catch (e) { console.error('[Mobeus] ready handler threw', e); }
  }

  /**
   * Override config keys after embed.js loads, before pre-warm fires.
   *
   * Reliable keys: `preWarm`, `connectOnOpen`, `toolCalls`, `citations`,
   * `debug`, `scene`, `theme` — all of them only until pre-warm fires, this
   * function included: `startPreWarm()` sets `preWarmFired` unconditionally,
   * even when `preWarm` is false, and every later call is refused. Set
   * `connectOnOpen` in `data-config`, or here synchronously after the
   * embed.js tag, exactly like the rest.
   * Changing `render` post-bootstrap is not supported because the
   * render adapter is already mounted — the call is rejected with a warning.
   * `position` only affects initial render; changing it via `configure` has
   * no effect on the already-mounted adapter and is also rejected. `mode` is
   * rejected too: the voice built-ins are already installed by this point.
   *
   * Must be called BEFORE pre-warm (i.e. synchronously after the embed.js
   * `<script>` tag, or inside a script tag that runs before DOMContentLoaded
   * resolves). After pre-warm fires, this is a no-op with `console.warn`.
   */
  function configure(partial: Partial<EmbedConfig>): void {
    if (preWarmFired) {
      console.warn('[Mobeus] configure() called after pre-warm; ignoring');
      return;
    }
    if (!partial || typeof partial !== 'object') return;
    const next: EmbedConfig = { ...config };
    for (const k of Object.keys(partial) as Array<keyof EmbedConfig>) {
      const v = partial[k];
      if (v === undefined) continue;
      if (k === 'render') {
        console.warn('[Mobeus] configure(): cannot change render mode after bootstrap; ignoring');
        continue;
      }
      if (k === 'position') {
        console.warn('[Mobeus] configure(): cannot change position after bootstrap; ignoring');
        continue;
      }
      if (k === 'inputStart') {
        console.warn('[Mobeus] configure(): cannot change inputStart after bootstrap; set it in data-config. Ignoring');
        continue;
      }
      if (k === 'mode') {
        // Rejected for the same reason as `render`: the voice built-ins are
        // installed and `chatMode` is captured during bootstrap, long before
        // this can run. Accepting it would leave `diagnostic()` reporting
        // chat mode while the mic prompt still fires and the avatar still
        // streams — worse than refusing. Set it in `data-config`.
        console.warn('[Mobeus] configure(): cannot change mode after bootstrap; set it in data-config. Ignoring');
        continue;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (next as any)[k] = v;
    }
    config = next;
  }

  function diagnostic(): MobeusDiagnostic {
    return {
      version: VERSION,
      protocolVersion: PROTOCOL_VERSION,
      config: { ...config },
      registeredSiteFunctions: registry
        .serializeManifest({ scene: config.scene, theme: config.theme })
        .map((e) => e.name),
      sessionState,
      roomName,
      isReady,
    };
  }

  const Mobeus = {
    version: VERSION,
    PROTOCOL_VERSION,
    __initialized: true,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    registerSiteFunction(name: string, entry: any) {
      registry.registerSiteFunction(name, entry, { scene: config.scene, theme: config.theme });
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    registerSiteFunctions(manifest: Record<string, any>) {
      for (const [name, entry] of Object.entries(manifest)) {
        registry.registerSiteFunction(name, entry, { scene: config.scene, theme: config.theme });
      }
    },
    registerSceneHandler(fn: (dsl: string) => void) { registry.registerSceneHandler(fn); },
    registerThemeHandler(fn: (t: 'light' | 'dark' | 'system') => void) { registry.registerThemeHandler(fn); },
    tellAgent: (m: string) => pushApi.tellAgent(m),
    informAgent: (m: string) => pushApi.informAgent(m),
    askAgent: (m: string) => pushApi.askAgent(m),
    agentSay: (m: string) => pushApi.agentSay(m),
    transferAgent: (name: string, opts?: { preserveHistory?: boolean }) => pushApi.transferAgent(name, opts),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    on: (event: string, h: any) => eventBus.on(event as any, h),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    off: (event: string, h: any) => eventBus.off(event as any, h),
    connect: (): Promise<void> => connectAndDispatch(),
    disconnect: (): Promise<void> => disconnectAndUpdate(),
    configure,
    diagnostic,
    ready(fn: () => void) {
      if (typeof fn !== 'function') return;
      // After the initial microtask drain, fire immediately. Otherwise queue
      // so synchronous registrations in the same script tick are captured
      // before pre-warm fires.
      if (isReady) fireReadyHandler(fn);
      else readyQueue.push(fn);
    },
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).Mobeus = Mobeus;

  // Drain ready queue on next microtask, then flip the isReady flag so any
  // late-bound Mobeus.ready(fn) calls fire immediately instead of queuing.
  //
  // Pre-warm is deferred to DOMContentLoaded (or fired immediately if the
  // document is already parsed). The microtask scheduled here drains
  // BETWEEN the embed.js script tag and subsequent inline scripts in the
  // page, so if pre-warm ran in the microtask it would beat any
  // Mobeus.registerSiteFunction() calls in those later inline tags. Waiting
  // for DOMContentLoaded gives every parser-blocking script a chance to
  // register before the manifest is serialized for /prepare.
  function startPreWarm() {
    // Always mark pre-warm as "fired" so `configure(...)` after this point
    // is a no-op, regardless of whether we actually opened a session.
    preWarmFired = true;
    if (!config.preWarm || inputStart) return;
    void connectAndDispatch();
  }
  queueMicrotask(() => {
    for (const fn of readyQueue) fireReadyHandler(fn);
    readyQueue.length = 0;
    isReady = true;
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', startPreWarm, { once: true });
    } else {
      // Document already parsed (embed.js was loaded async/defer/dynamically).
      // queueMicrotask gives the customer a chance to chain
      // Mobeus.registerSiteFunction() right after embed.js loads.
      queueMicrotask(startPreWarm);
    }
  });
}
